/**
 * Performance Monitoring Utilities
 * 
 * Provides tools to measure and track performance improvements
 * Helps validate that optimizations are working
 */

import { logger } from './logger';

interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

interface PerformanceReport {
  metrics: PerformanceMetric[];
  summary: {
    totalDuration: number;
    averageDuration: number;
    slowestOperation: string;
    fastestOperation: string;
  };
}

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, PerformanceMetric> = new Map();
  private enabled: boolean = false;

  constructor() {
    if (PerformanceMonitor.instance) {
      return PerformanceMonitor.instance;
    }
    
    // Enable monitoring in development or when explicitly enabled
    this.enabled = 
      import.meta.env.DEV || 
      localStorage.getItem('performance.monitoring') === 'true';
    
    PerformanceMonitor.instance = this;
  }

  /**
   * Start timing an operation
   */
  startTiming(name: string, metadata?: Record<string, any>): void {
    if (!this.enabled) return;

    const metric: PerformanceMetric = {
      name,
      startTime: performance.now(),
      metadata,
    };

    this.metrics.set(name, metric);
    logger.debug(`⏱️ Performance: Started timing "${name}"`);
  }

  /**
   * End timing an operation
   */
  endTiming(name: string): number | null {
    if (!this.enabled) return null;

    const metric = this.metrics.get(name);
    if (!metric) {
      logger.warn(`⏱️ Performance: No timing found for "${name}"`);
      return null;
    }

    metric.endTime = performance.now();
    metric.duration = metric.endTime - metric.startTime;

    logger.debug(
      `⏱️ Performance: "${name}" took ${metric.duration.toFixed(2)}ms`,
      metric.metadata
    );

    return metric.duration;
  }

  /**
   * Measure a function's execution time
   */
  async measure<T>(
    name: string,
    fn: () => Promise<T> | T,
    metadata?: Record<string, any>
  ): Promise<T> {
    if (!this.enabled) {
      return await fn();
    }

    this.startTiming(name, metadata);
    try {
      const result = await fn();
      this.endTiming(name);
      return result;
    } catch (error) {
      this.endTiming(name);
      throw error;
    }
  }

  /**
   * Get all metrics
   */
  getMetrics(): PerformanceMetric[] {
    return Array.from(this.metrics.values()).filter(m => m.duration !== undefined);
  }

  /**
   * Generate a performance report
   */
  generateReport(): PerformanceReport {
    const completedMetrics = this.getMetrics();
    
    if (completedMetrics.length === 0) {
      return {
        metrics: [],
        summary: {
          totalDuration: 0,
          averageDuration: 0,
          slowestOperation: 'N/A',
          fastestOperation: 'N/A',
        },
      };
    }

    const durations = completedMetrics.map(m => m.duration!);
    const totalDuration = durations.reduce((sum, d) => sum + d, 0);
    const averageDuration = totalDuration / durations.length;

    const slowest = completedMetrics.reduce((prev, curr) =>
      (curr.duration! > prev.duration!) ? curr : prev
    );
    const fastest = completedMetrics.reduce((prev, curr) =>
      (curr.duration! < prev.duration!) ? curr : prev
    );

    return {
      metrics: completedMetrics,
      summary: {
        totalDuration,
        averageDuration,
        slowestOperation: `${slowest.name} (${slowest.duration!.toFixed(2)}ms)`,
        fastestOperation: `${fastest.name} (${fastest.duration!.toFixed(2)}ms)`,
      },
    };
  }

  /**
   * Log performance report to console
   */
  logReport(): void {
    if (!this.enabled) return;

    const report = this.generateReport();
    
    console.group('📊 Performance Report');
    console.table(report.metrics.map(m => ({
      Operation: m.name,
      Duration: `${m.duration!.toFixed(2)}ms`,
      ...m.metadata,
    })));
    console.log('Summary:', report.summary);
    console.groupEnd();
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics.clear();
    logger.debug('⏱️ Performance: Cleared all metrics');
  }

  /**
   * Enable or disable monitoring
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    localStorage.setItem('performance.monitoring', enabled.toString());
    logger.info(`⏱️ Performance monitoring ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if monitoring is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Compare cache performance
   */
  compareCachePerformance(
    oldCacheTime: number,
    newCacheTime: number
  ): { improvement: number; percentageBetter: number } {
    const improvement = oldCacheTime - newCacheTime;
    const percentageBetter = (improvement / oldCacheTime) * 100;

    logger.info(
      `⚡ Cache Performance Improvement: ${improvement.toFixed(2)}ms (${percentageBetter.toFixed(1)}% faster)`
    );

    return { improvement, percentageBetter };
  }

  /**
   * Track network requests
   */
  trackNetworkRequest(url: string, startTime: number, endTime: number): void {
    if (!this.enabled) return;

    const duration = endTime - startTime;
    const name = `network_${new URL(url, window.location.origin).pathname}`;
    
    this.metrics.set(name, {
      name,
      startTime,
      endTime,
      duration,
      metadata: { url, type: 'network' },
    });

    logger.debug(`🌐 Network: ${url} took ${duration.toFixed(2)}ms`);
  }

  /**
   * Get singleton instance
   */
  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }
}

// Export singleton instance
export const performanceMonitor = PerformanceMonitor.getInstance();

// Export utility functions for easier use
export const startTiming = (name: string, metadata?: Record<string, any>) =>
  performanceMonitor.startTiming(name, metadata);

export const endTiming = (name: string) =>
  performanceMonitor.endTiming(name);

export const measure = <T>(
  name: string,
  fn: () => Promise<T> | T,
  metadata?: Record<string, any>
) => performanceMonitor.measure(name, fn, metadata);

export const logPerformanceReport = () =>
  performanceMonitor.logReport();